import {
  Component,
  HostBinding,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CampaignSponsorshipField } from '@fmnts/api/next';
import { FmntsFormFieldModule } from '@fmnts/components/form-field';
import { FmntsLayoutModule } from '@fmnts/components/layout';
import { FmntsRadioModule } from '@fmnts/components/radio';
import { Option } from '@fmnts/core';
import {
  AbstractDeclarativeInputFieldComponent,
  ErrorMessagePipe,
  TemplateBuilder,
  interpolateTemplate,
} from '@fmnts/declarative-forms';
import {
  I18nModule,
  InlineTranslateable,
  TranslationService,
} from '@fmnts/i18n';
import { LetDirective } from '@ngrx/component';
import { Observable, catchError, of } from 'rxjs';

type SponsorshipItem = CampaignSponsorshipField['widgetOptions']['items'][0];
type FieldValue = Option<unknown>;

@Component({
  selector: 'next-sponsorship-field',
  templateUrl: './sponsorship-field.component.html',
  styleUrls: ['./sponsorship-field.component.scss'],
  standalone: true,
  imports: [
    ErrorMessagePipe,
    FmntsFormFieldModule,
    FmntsLayoutModule,
    FmntsRadioModule,
    FormsModule,
    I18nModule,
    LetDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class SponsorshipFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    CampaignSponsorshipField,
    FieldValue
  >
  implements OnInit
{
  private readonly translationService = inject(TranslationService);
  private readonly templateBuilder = inject(TemplateBuilder);

  @HostBinding('class') componentClass = 'form-field form-field__sponsorship';

  get value(): FieldValue {
    return this._value();
  }
  set value(value: FieldValue) {
    this._value.set(value);
    this._onChange(this._value());
    this._onTouched();
  }
  private _value = signal<FieldValue>(null);

  protected items = signal<SponsorshipItem[]>([]);

  ngOnInit(): void {
    this.items.set(this.field.widgetOptions.items);
  }

  writeValue(newValue: unknown) {
    this._value.set(newValue);
  }

  /**
   * @param description translateable String
   * @returns Observable with the HTML content
   */
  protected htmlContentFromField$(
    description: InlineTranslateable,
  ): Observable<string> {
    return this.translationService.fromInlineTranslation$(of(description)).pipe(
      interpolateTemplate((part) =>
        this.templateBuilder
          .interpolate(part)
          // Catch any error and render an empty string
          .pipe(catchError((e) => of(''))),
      ),
    );
  }

  get showError(): boolean {
    return this.control.touched && this.control.invalid;
  }
}
